import React, { FC } from "react";
import type { GridRow, GridColumn } from "@getregistered/greg-editors/dist/lib/domain/grid/types";
import { ActionMenu, ActionList, IconButton, ThemeProvider } from "@primer/react";
import { CheckCircleIcon, ClockIcon, CreditCardIcon, KebabHorizontalIcon } from "@primer/octicons-react";

interface EventCardProps {
  row: GridRow;
  columns: GridColumn[];
}

interface ActionButton {
  href: string;
  text: string;
  key: string;
  method?: string;
  "data-confirm"?: string;
}

function getCellValue(row: GridRow, key: string): string {
  const val = row[key];
  if (val === null || val === undefined) return "--";
  if (typeof val === "object" && "displayValue" in val) return val.displayValue;
  return String(val);
}

function getHref(row: GridRow, key: string): string | null {
  const val = row[key];
  if (val && typeof val === "object" && "cellVariant" in val) {
    const variant = val.cellVariant;
    if (variant?.type === "drilldown" && variant.href) return variant.href;
  }
  return null;
}

function getStatusInfo(row: GridRow): { label: string; scheme: string } {
  const val = row["events:status"] as Record<string, any> | null | undefined;
  if (val && typeof val === "object") {
    const label = val.displayValue || val.rawValue || "";
    const scheme = val.cellVariant?.scheme || "secondary";
    return { label, scheme };
  }
  return { label: "", scheme: "secondary" };
}

function getActionButtons(row: GridRow): ActionButton[] {
  const val = row["action_menu"] as Record<string, any> | null | undefined;
  if (val && Array.isArray(val.buttons)) return val.buttons as ActionButton[];
  return [];
}

export const EventCard: FC<EventCardProps> = ({ row }) => {
  const title = getCellValue(row, "events:title");
  const href = getHref(row, "events:title");
  const titleCell = (row["events:title"] as Record<string, any>) || {};
  const imageUrl = titleCell.imageUrl || null;
  const countdown = titleCell.countdownDisplay || null;
  const dateRange = getCellValue(row, "events:date_range");
  const registrations = getCellValue(row, "events:registrations");
  const revenue = getCellValue(row, "events:revenue");
  const status = getStatusInfo(row);
  const actions = getActionButtons(row);

  // Tint the (image-less) banner to match the status scheme. The matching
  // .event-card-header--<scheme> rules live in custom-event-card.scss; an event
  // with a banner image overrides the tint via the inline backgroundImage.
  const headerClass = ["event-card-header", status.scheme && `event-card-header--${status.scheme}`]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="event-card-inner test-event-card" data-event-id={row.id}>
      <div
        className={headerClass}
        style={imageUrl ? { backgroundImage: `url('${imageUrl}')` } : undefined}
      >
        {status.label && (
          <span className={`event-card-status event-card-status--${status.scheme}`}>
            <span className="event-card-status-dot" aria-hidden="true" />
            <span className="event-card-status-label">{status.label}</span>
          </span>
        )}
        {actions.length > 0 && (
          // The grid mounts EventCard inside greg-editors' bundled Primer
          // ThemeProvider, but this ActionMenu resolves to the app's own
          // @primer/react instance (separate Vite chunk) whose ThemeContext is
          // otherwise unpopulated, so useTheme() would crash on overlay open.
          // Wrap in the app instance's ThemeProvider so theming resolves.
          <ThemeProvider>
            <span className="event-card-menu">
              <ActionMenu>
                <ActionMenu.Anchor>
                  <IconButton
                    icon={KebabHorizontalIcon}
                    aria-label="Event Options"
                    variant="invisible"
                    size="small"
                    unsafeDisableTooltip
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay>
                  <ActionList>
                    {actions.map((action) => (
                      <ActionList.LinkItem
                        key={action.key}
                        href={action.href}
                        {...(action.method ? { "data-turbo-method": action.method } : {})}
                        {...(action["data-confirm"] ? { "data-turbo-confirm": action["data-confirm"] } : {})}
                      >
                        {action.text}
                      </ActionList.LinkItem>
                    ))}
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </span>
          </ThemeProvider>
        )}
      </div>
      <div className="event-card-body">
        <div className="event-card-body-main">
          <div className="event-card-title">
            {href ? (
              <a href={href} className="event-card-link">
                {title}
              </a>
            ) : (
              title
            )}
          </div>
          <ol>
            <li className="breadcrumb-item breadcrumb-item-meta breadcrumb-item-no-border text-semibold f6">
              {dateRange}
            </li>
          </ol>
        </div>
      </div>
      <div className="event-card-footer">
        {countdown && (
          <div className="event-card-footer-item">
            <ClockIcon size={16} className="color-fg-muted" />
            <span className="f6 text-semibold color-fg-default">{countdown}</span>
          </div>
        )}
        <div className="event-card-footer-item">
          <CheckCircleIcon size={16} className="color-fg-muted" />
          <span className="f6 text-semibold color-fg-default">{registrations}</span>
        </div>
        {revenue !== "--" && (
          <div className="event-card-footer-item no-border">
            <CreditCardIcon size={16} className="color-fg-muted" />
            <span className="f6 text-semibold color-fg-default">{revenue}</span>
          </div>
        )}
      </div>
    </div>
  );
};
